/**
 * GesamtfreigabeDialog - Konzept 8.4: warn when a file is switched off.
 *
 * Names the document AND the task it was switched off for - the same file can be off
 * for one task and on for another, so the filename alone does not locate the decision.
 * "Zur Aufgabe" opens that task, so the warning can be acted on rather than only
 * acknowledged. Non-blocking: switching a file off is usually deliberate, so the
 * primary action stays "Trotzdem freigeben".
 *
 * The check and the footer button live in BearbeitungsMaske. The
 * attachments_warning_shown/acknowledged flags are backend (E3) and are not claimed here.
 *
 * [Source: docs/eingangsverarbeitung-epic.md §3 WS-E E3]
 */
import { AlertTriangle, ArrowRight, Paperclip } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../administration/components';

/** One deactivated file, with the Aufgabe it was deactivated for. */
export interface DeaktivierterAnhang {
  taskId: string;
  taskTitle: string;
  attachmentId: string;
  filename: string;
}

interface GesamtfreigabeDialogProps {
  open: boolean;
  /** The findings of the 8.4 check. Never empty — no findings means no dialog. */
  anhaenge: DeaktivierterAnhang[];
  /** «→ Zur Aufgabe»: select that task in T2 and close. */
  onJumpToTask: (taskId: string) => void;
  /** «Trotzdem freigeben» — the warning is explicitly non-blocking. */
  onConfirm: () => void;
  onCancel: () => void;
}

export function GesamtfreigabeDialog({
  open,
  anhaenge,
  onJumpToTask,
  onConfirm,
  onCancel,
}: GesamtfreigabeDialogProps) {
  const mehrere = anhaenge.length !== 1;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title="Nicht bestätigte Anhänge"
      description={
        mehrere
          ? `${anhaenge.length} Anhänge sind für den Versand deaktiviert und werden mit der Gesamtfreigabe NICHT mitgeschickt.`
          : 'Ein Anhang ist für den Versand deaktiviert und wird mit der Gesamtfreigabe NICHT mitgeschickt.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Abbrechen
          </Button>
          {/* Non-blocking by design (8.4 "alternativ ... trotz Warnung bestätigt"). */}
          <Button size="sm" onClick={onConfirm}>
            Trotzdem freigeben
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-2.5 rounded-md border border-sem-decide/30 bg-sem-decide-bg/50 p-3">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-sem-decide" />
        <p className="text-sm text-foreground">
          Prüfen Sie, ob das Absicht war. Über «Zur Aufgabe» gelangen Sie direkt zur
          betroffenen Aufgabe zurück.
        </p>
      </div>

      <ul className="mt-3 flex flex-col gap-1.5">
        {anhaenge.map((a) => (
          <li
            key={`${a.taskId}::${a.attachmentId}`}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
          >
            <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 leading-tight">
              {/* Verweis auf das betroffene Dokument … */}
              <p className="truncate text-sm font-medium text-foreground" title={a.filename}>
                {a.filename}
              </p>
              {/* … und auf die Aufgabe, für die es deaktiviert wurde. */}
              <p className="truncate text-xs text-muted-foreground">{a.taskTitle}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onJumpToTask(a.taskId)}
              className="shrink-0 text-muted-foreground"
            >
              Zur Aufgabe
              <ArrowRight className="size-4" />
            </Button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
